/**
 * @file permissions.js
 * @version 2.3.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-05-12
 * @modified 2026-10-30
 *
 * Client-side access helpers. The `user` object handed around the app comes
 * from /api/users/me (or /login) and already carries the caller's EFFECTIVE
 * permissions — their roles' permissions unioned, with per-user overrides
 * applied — so nothing here has to know about roles.
 *
 *   hasPermission(user, 'credit_control')          // true / false
 *   hasAnyPermission(user, ['a', 'b'])             // holds at least one
 *   badgeOf(user)                                  // 'ADMIN' | 'USER' | 'REP'
 *   isReadOnly(user)                               // REP: view-only everywhere
 *   canWrite(user, 'orders.edit')                  // permission AND not read-only
 *
 * Mirrors the server exactly:
 *   server: req.user.is_admin || req.user.permissions?.[key] === true
 *   client: user.is_admin     || user.permissions?.[key]     === true
 *
 * These are presentation, not enforcement. Every write is independently gated
 * server-side (middleware/readOnly.js and requirePermission); hiding a button a
 * user cannot use is a courtesy, not the boundary.
 *
 * 2.2.0 (2026-10-28, Cam — P7): 'rep_pricing.' — a rep may type a price, pull
 * a line back and flag it for the office on their own pricing screen
 * (canWrite(user, 'rep_pricing.respond')), matching the readOnly.js carve-out.
 * 2.3.0 (2026-10-30, Cam — 29 Oct): 'rep_order_requests.' — a rep sends the
 * office an order / standing order request (or a change) and withdraws it
 * (canWrite(user, 'rep_order_requests.send')), matching the carve-out.
 */

export function hasPermission(user, key) {
  if (!user) return false;
  if (user.is_admin === true) return true;
  return !!(user.permissions && user.permissions[key] === true);
}

export function hasAnyPermission(user, keys) {
  if (!user) return false;
  if (user.is_admin === true) return true;
  return (keys || []).some(k => hasPermission(user, k));
}

export function badgeOf(user) {
  if (!user) return 'USER';
  if (user.is_admin === true) return 'ADMIN';
  return user.badge || 'USER';
}

/**
 * True when the account may look but not touch. Today that is the REP badge and
 * only the REP badge; the flag is read from the server rather than derived from
 * the badge name so a future read-only badge needs no client change.
 */
export function isReadOnly(user) {
  if (!user) return false;
  if (user.is_admin === true) return false;
  return user.read_only === true || user.badge === 'REP';
}

/**
 * The Blend Calculator is the one place a read-only account acts, and Ask the
 * Data is the other — a POST that only reads. Permissions whose key starts with
 * one of these are honoured even in read-only mode, which matches the carve-out
 * list in middleware/readOnly.js — if you add one there, add it here or the
 * button will stay hidden on a request the server allows.
 *
 * These are PERMISSION KEYS, so only a carve-out gated on a permission has an
 * entry. The permission-less ones (`permission: null` there) have nothing to
 * list and are never routed through canWrite: own dashboard layout, a customer
 * note, and snoozing an account on the Gone Quiet rep widget
 * (/rep-dashboard/lapsed-accounts/:id/snooze). Their client half is instead that
 * the control does NOT carry `data-write-action`, and does not sit in a modal
 * footer without `data-read-safe` — see App.css. Do not add a prefix here for
 * one of those: a key like 'rep_dashboard.' would silently open every future
 * permission that happens to share it.
 */
const READ_ONLY_WRITE_PREFIXES = ['blend_calculator', 'blend_costs', 'blends.', 'data_query.', 'rep_pricing.', 'rep_order_requests.'];

/**
 * The check to use on an action button: the user holds the permission AND is
 * not view-only. Use hasPermission for anything that merely reveals a screen.
 */
export { READ_ONLY_WRITE_PREFIXES };

export function canWrite(user, key) {
  if (!hasPermission(user, key)) return false;
  if (!isReadOnly(user)) return true;
  return READ_ONLY_WRITE_PREFIXES.some(p => key.startsWith(p));
}

/**
 * How the app should treat Xero for this account.
 *   'required' — unusable until connected (everyone in the office)
 *   'blocked'  — never mention it, never offer it (reps)
 *   'optional' — offer it, don't insist
 */
export function xeroRequirement(user) {
  return user?.xero || 'required';
}

/** The permission that governs a menu id, or a tab within one. */
export function navPermission(menuId, tabKey) {
  return tabKey ? `nav.${menuId}.${tabKey}` : `nav.${menuId}`;
}
